import { useMemo, useEffect, useState, useCallback } from 'react'
import { Body, Bold } from '@panwds/react-ui'
import { Textarea } from '@panwds/react-form'
import { groupBy } from 'lodash'
import { useFormContext } from 'react-hook-form'
import UAccordionHeader from './UAccordionHeader'
import {
  APP_ID_NET_INTER_CONNECT,
  APP_ID_PRIVATE_APPS,
  APP_ID_PRIVATE_APPS_QTY,
  APP_ID_DATA_TRANSFER,
  APP_ID_AUTONOMOUS_DEM,
  APP_ID_IOT,
  APP_ID_DLP,
  APP_ID_CUSTOMER_SUCCESS,
  APP_ID_SDWAN
} from '../common/GlobalConstants'
import { ACTIVATION_UI_ERROR_CODES, ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from '../../ErrorCodes'
import { FF_SDWAN_TID_REMOVE, FF_LICENSE_DESCRIPTION } from '../../../common/utils/FeatureFlags'
import { getVarValue } from '../../../common/utils/Sparky'

import type { VFC, ChangeEvent } from 'react'
import type { CommonStoreState, SubLicense } from '../../store'
import type { Actions } from '../../actions'
import type { AppManifest } from '@sparky/framework'

const NONE_ERROR = { isError: false, message: '' }

const sdwanTidRemove = getVarValue(FF_SDWAN_TID_REMOVE, false)
const licenseDescription = getVarValue(FF_LICENSE_DESCRIPTION, false)

const ADD_ON_APP_IDS = new Set([
  APP_ID_NET_INTER_CONNECT,
  APP_ID_PRIVATE_APPS,
  APP_ID_PRIVATE_APPS_QTY,
  APP_ID_DATA_TRANSFER,
  APP_ID_AUTONOMOUS_DEM,
  APP_ID_IOT,
  APP_ID_DLP,
  APP_ID_CUSTOMER_SUCCESS])

type ULicenseProps = {
  state: CommonStoreState
  actions: Actions
  updateTitle: (title: string) => void
}

const ULicense: VFC<ULicenseProps> = (props) => {
  const { state, actions } = props
  const { manifest, licenses, apps, absoluteRootTsg, selectedPlatformRegion, supportAccountId } = state
  const { updateActivationInstances, updateActivationData } = actions
  const { setError, clearErrors } = useFormContext()

  const [description, setDescription] = useState<string | undefined>(undefined)
  const [error, setComponentError] = useState<{ isError: boolean, message: string }>(NONE_ERROR)
  const primaryAppId = useMemo<string>(() => {
    if (!manifest) {
      return ''
    }
    return manifest.primary_app_id
  }, [manifest])

  const primaryLicense = useMemo<SubLicense[] | undefined>(() => {
    return primaryAppId ? licenses?.get(primaryAppId)?.licenses : []
  }, [primaryAppId, licenses])

  // const primaryAppManifest = useMemo<AppManifest>(() => {
  //   return apps.get(primaryAppId)
  // }, [apps, primaryAppId])

  const showDescription = useMemo(() => {
    return licenseDescription && primaryAppId === APP_ID_SDWAN
  }, [primaryAppId])

  const renderedLicenseDisplay = useMemo(() => {
    if (primaryAppId === APP_ID_SDWAN) {
      return null
    }
    if (!primaryLicense) {
      return <Body>No License</Body>
    }
    return primaryLicense.map((each, index) => {
      return (
        <div className='cm-w-1/2 cm-flex cm-justify-between' key={index}>
          <Body appearance='primary'><Bold>{each.license_display_name}</Bold></Body>
          <Body appearance='secondary'>{each.purchased_size} {each.purchased_unit}</Body>
        </div>
      )
    })
  }, [primaryAppId, primaryLicense])

  /**
   * This function checks state.absoluteRootTsg, and see if user has access, and see if this activation needs root activation or not
   * @param appId
   * @param manifest
   * @returns boolean
   */
  const checkActivateOnRoot = useCallback((appId: string, manifest: AppManifest) => {
    setComponentError(NONE_ERROR)
    clearErrors(primaryAppId)
    if (absoluteRootTsg?.error?.startsWith('cannot get absolute root tsg')) {
      const msg = ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.NO_ABSOLUTE_TSG_ACCESS]
      setComponentError({
        isError: true,
        message: msg
      })
      setError(primaryAppId, { type: 'custom', message: msg })
      return false
    }
    if (absoluteRootTsg?.error !== '' && absoluteRootTsg?.tsgId) {
      if (appId === APP_ID_SDWAN) {
        const rootSdwanInst = absoluteRootTsg.instances.get(appId)
        let cspTidInLic: string | undefined
        if (licenses?.get(primaryAppId)?.license_id) {
          // in licenses, use csp_tenant_id
          cspTidInLic = licenses?.get(primaryAppId)?.csp_tenant_id
          if (!rootSdwanInst) {
            // activate from License, cannot pick another tree that has no SDWAN (Tree Locking)
            const msg = ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.TREE_LOCK]
            setComponentError({
              isError: true,
              message: msg
            })
            setError(primaryAppId, { type: 'custom', message: msg })
            return false
          }
        }
        else {
          // in hash, use tenant_id inside entitlement
          cspTidInLic = primaryLicense?.find(each => each.app_id === APP_ID_SDWAN)?.tenant_id as string
        }
        if (!rootSdwanInst) {
          // no sdwan running on root, activate it
          return true
        }
        if (sdwanTidRemove) {
          if (rootSdwanInst.support_account_id !== supportAccountId) {
            const msg = ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.CSP_LOCK]
            setComponentError({
              isError: true,
              message: msg
            })
            setError(primaryAppId, { type: 'custom', message: msg })
            return false
          }
        }
        else {
          if (rootSdwanInst.csp_tenant_id === cspTidInLic) {
            // check license source
            // sdwan running on root, and entitlement in license is same as root, skip root activation
            return false
          }
          else {
            // sdwan running on root different than license, set error
            const msg = ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.INSTANCE_FROM_DIFF_SOURCE]
            setComponentError({
              isError: true,
              message: msg
            })
            setError(primaryAppId, { type: 'custom', message: msg })
            return false
          }
        }
      }
    }
    return false
  }, [absoluteRootTsg?.error, absoluteRootTsg?.instances, absoluteRootTsg?.tsgId, clearErrors, primaryAppId, primaryLicense, setError, licenses, supportAccountId])

  useEffect(() => {
    if (!primaryAppId) {
      return
    }
    const groupedLicense = groupBy(primaryLicense, 'app_id')

    const instances: {
      [key: string]: any
    } = {}
    for (const [appId, value] of Object.entries(groupedLicense)) {
      let activateOnRoot: boolean = false
      if (apps.get(appId)?.flags?.is_root_on_boarding_required) {
        // only sdwan is on today
        activateOnRoot = checkActivateOnRoot(appId, apps.get(appId))
      }
      const instance = {
        app_id: appId,
        activation_type: 'NEW',
        is_disabled: ADD_ON_APP_IDS.has(appId as string) ? false : undefined,
        parent_app_id: ADD_ON_APP_IDS.has(appId as string) ? primaryAppId : undefined,
        activate_on_root: activateOnRoot ?? undefined,
        license_id: licenses?.get(appId)?.license_id,
        description: description,
        allocation: value.map(each => {
          return {
            app_id: each.app_id,
            license_type: each.license_type ?? appId,
            allocation_size: each.purchased_size ?? 0,
            tenant_id: 'new',
          }
        })
      }
      instances[appId] = instance
    }

    updateActivationInstances({
      ...instances,
    })
  }, [apps, checkActivateOnRoot, description, licenses, primaryAppId, primaryLicense, updateActivationData, updateActivationInstances])

  return (
    <div>
      <UAccordionHeader title='Licenses' subtitle='' />
      <div className='cm-mt-2'>
        {renderedLicenseDisplay}
        {showDescription &&
          <div className='cm-p-2'>
            <Textarea
              name="name"
              label="Description"
              appearance="default"
              register={{
                required: false,
                onChange: (event: ChangeEvent<HTMLTextAreaElement>) => {
                  setDescription(event.target.value)
                }
              }}
            />
          </div>}
      </div>
      {error.isError && <Body size="sm" appearance={'error'} addClassName='cm-items-center cm-py-1.5 cm-pl-1'>
        {`${error.message}, please select another TSG`}</Body>}
    </div>
  )
}

export default ULicense
